/**
 * Helpers for detecting and repairing a stale collaborative (OT) draft.
 *
 * A page's published content can advance without the shared OT draft document
 * being touched — most commonly when content is published through the public
 * API, which never writes into the OT draft (the draft is only ever mutated by
 * operations coming through the web app's socket connection). When that
 * happens the draft the editor is bound to no longer reflects the latest
 * published version.
 *
 * To detect this we stamp the published `contentRevision` the draft is in sync
 * with onto the OT document's root node. `setRootAttribute` submits a real OT
 * operation, so the stamp propagates to the shared document for every client
 * and persists — no publish required. On load (and on the live socket event)
 * we compare the stamp against the latest known `contentRevision`; a mismatch
 * means the draft is stale.
 */

// Root-node attribute recording the published revision the draft is in sync
// with. We store the page's `contentRevision` here.
//
// IMPORTANT: this MUST be one of the ot-client's hard-coded, whitelisted root
// attributes. `RootVNode` (in @teamwork/ot-client) strips any root attribute
// not in its frozen `attributeNames` list on every DOM<->model sync, so an
// arbitrary name (e.g. `@data-tw-content-revision`) is written momentarily and
// then silently dropped. The allowed names are `@data-tw-schema-hash`,
// `@data-tw-schema-time`, `@data-tw-version-number` and `@data-tw-version-time`.
// We reuse `@data-tw-version-number` — the slot meant for "the published
// version number of the document's content" — which is also the attribute
// notebooks uses for the same purpose.
export const OT_DRAFT_CONTENT_REVISION_ATTR = '@data-tw-version-number';

function hasOtAttributeApi(otClient) {
  return !!otClient
    && typeof otClient.getRootAttribute === 'function'
    && typeof otClient.setRootAttribute === 'function';
}

/**
 * Reads the published revision currently stamped on the draft, or null when
 * the draft has never been stamped (a legacy/not-yet-bootstrapped draft).
 */
export function getStampedContentRevision(otClient) {
  if (!hasOtAttributeApi(otClient)) {
    return null;
  }

  return otClient.getRootAttribute(OT_DRAFT_CONTENT_REVISION_ATTR);
}

/**
 * Stamps the published revision the draft now corresponds to. This is an OT
 * operation and propagates to the shared document for all clients.
 */
export function stampContentRevision(otClient, contentRevision) {
  if (!hasOtAttributeApi(otClient)) {
    return;
  }

  if (contentRevision === undefined || contentRevision === null) {
    return;
  }

  otClient.setRootAttribute(OT_DRAFT_CONTENT_REVISION_ATTR, String(contentRevision));
}

/**
 * Whether the draft is based on an older published version than the latest.
 *
 * An empty stamp is intentionally NOT treated as outdated: a never-stamped
 * draft must be left untouched so any unpublished draft edits are preserved.
 * The caller bootstraps the stamp in that case instead of replacing content.
 */
export function isDraftOutdated(stampedRevision, latestContentRevision) {
  if (stampedRevision === null || stampedRevision === undefined || stampedRevision === '') {
    return false;
  }

  if (latestContentRevision === null || latestContentRevision === undefined) {
    return false;
  }

  return String(stampedRevision) !== String(latestContentRevision);
}
